import { styled, useTheme } from "@mui/material/styles";
import {
  AcquireBookDataGrid,
  buildSelfAcquiredInitialState,
  OTHER_USER_RESOURCE_FIELD,
  SELF_ACQUIRED_OPERATOR_VALUE,
  OTHER_USER_ACQUIRED_OPERATOR_VALUE,
} from "@/components/DataGrid";
import {
  type GridColDef,
  type GridFilterOperator,
  type GridRenderCellParams,
  type GridRowId,
} from "@mui/x-data-grid-pro";
import { useApiClient } from "../../../hooks/useApiClient";
import { type AxiosInstance } from "axios";
import useSWR from "swr";
import { ButtonVariantOption } from "../../../components/Button";
import {
  Box,
  Card,
  CardContent,
  Chip,
  Stack,
  Typography,
  useMediaQuery,
  Button as MuiButton,
} from "@mui/material";
import { ReadOnlyTooltip } from "@/components/Tooltip/ReadOnlyTooltip";
import { ButtonMenu } from "../../../components/Menu/ButtonMenu";
import { MenuItem } from "../../../components/Menu/MenuItem";
import { useFeatureFlags } from "@/configs/featureFlag";
import { format } from "date-fns";
import ReadMoreIcon from "@mui/icons-material/ReadMore";
import DownloadIcon from "@mui/icons-material/Download";
import CheckIcon from "@mui/icons-material/Check";
import { useContext } from "react";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { useZipDownloadUrl } from "@/features/acquireBookList/api/getZipDownloadUrl";
import { useReadOnlyUser } from "@/hooks/useReadOnlyUser";
import * as Sentry from "@sentry/react";
import { z } from "zod";
import { userDataContext } from "@/hooks/useUserData";
import { formatNumberWithComma } from "@/utils/formatNumber";
import { type AcquiredRequest, type AcquiredPropertySearch } from "../types";

import { MobileList, CardRow } from "./MobileList";

export interface RequestFeedGridProps {
  setSelectedId: React.Dispatch<React.SetStateAction<GridRowId>>;
  setOpenMonitoringExecuteConfirmDialog: React.Dispatch<
    React.SetStateAction<boolean>
  >;
  setChargeCountClickedMonitoringExecuteButton: React.Dispatch<
    React.SetStateAction<number | null>
  >;
}

type AcquireBookStatusDataGridRow = AcquiredRequest;

interface PictureRequestAPIRequestStatus {
  id: number;
  task_id: string;
  status: string;
  name: string;
  requestPictureCount: number;
  requestedAt: string;
  finishedAt: string;
  createdAt: Date;
  updatedAt: Date;
  isOtherUserResource: boolean;
}

interface PictureRequestAPIResponse {
  count: number;
  list: PictureRequestAPIRequestStatus[];
}

const otherUserResourceFilterOperators: Array<
  GridFilterOperator<AcquireBookStatusDataGridRow, boolean>
> = [
  {
    label: "自分で取得",
    value: SELF_ACQUIRED_OPERATOR_VALUE,
    requiresFilterValue: false,
    getApplyFilterFn:
      () =>
      ({ row }) =>
        !row.isOtherUserResource,
  },
  {
    label: "他ユーザー取得",
    value: OTHER_USER_ACQUIRED_OPERATOR_VALUE,
    requiresFilterValue: false,
    getApplyFilterFn:
      () =>
      ({ row }) =>
        row.isOtherUserResource,
  },
];

const pictureRequestListContractSchema = z
  .object({
    list: z.array(
      z
        .object({
          isOtherUserResource: z.boolean(),
        })
        .passthrough()
    ),
  })
  .passthrough();

const validatePictureRequestListContract = (
  payload: unknown,
  endpoint: string
): void => {
  const parsed = pictureRequestListContractSchema.safeParse(payload);
  if (parsed.success) return;

  const error = new Error(`API contract violation at ${endpoint}`);
  Sentry.captureException(error, {
    extra: {
      endpoint,
      issues: parsed.error.issues,
    },
  });
  throw error;
};

const StyledDataGrid = styled(AcquireBookDataGrid)`
  // テーブルのセルをクリックしても青い枠が表示されないようにする
  & .MuiDataGrid-cell:focus-within,
  & .MuiDataGrid-cell:focus {
    outline: none !important;
  }

  // テーブルのカラムヘッダをクリックしても青い枠が表示されないようにする
  & .MuiDataGrid-columnHeader:focus-within,
  & .MuiDataGrid-columnHeader:focus {
    outline: none !important;
  }

  // テーブルヘッダーの背景色を設定
  .MuiDataGrid-columnHeaders {
    line-height: 1.5em !important;
  }

  // テーブルタイトルを太字に
  .MuiDataGrid-columnHeaderTitle {
    font-weight: 700;
  }
`;

const RequestStatusChip: React.FC<{ status: string }> = ({ status }) => {
  if (status === undefined) return null;
  switch (status) {
    case "REQUESTING":
      return <Chip label="依頼中" size="small" color="info" />;
    case "SUCCESS":
      return <Chip label="成功" size="small" color="success" />;
    case "SOME_FAILED":
      return <Chip label="失敗あり" size="small" color="warning" />;
  }
  return null;
};

const makeGridColDef = (
  realEstateRegistrationMonitoring: boolean,
  isSharableUser: boolean,
  setSelectedId: React.Dispatch<React.SetStateAction<GridRowId>>,
  setAcquireBookOpen: React.Dispatch<React.SetStateAction<boolean>>,
  setChargeCountClickedMonitoringExecuteButton: React.Dispatch<
    React.SetStateAction<number | null>
  >,
  handleDownload: (id: GridRowId) => Promise<void>,
  isReadOnly: boolean,
  returnPath: string,
  userId: string
): {
  columns: GridColDef[];
  renderActions: (row: AcquiredRequest) => React.ReactNode;
} => {
  const actionColumnMinWidth = realEstateRegistrationMonitoring ? 400 : 200;

  const renderActions = (row: AcquiredRequest): React.ReactNode => {
    const id = row.id;
    return (
      <Stack
        direction="row"
        spacing={1}
        useFlexGap
        flexWrap="wrap"
        sx={{ justifyContent: "flex-end", alignItems: "center" }}
      >
        <MuiButton
          variant={ButtonVariantOption.Outlined}
          component={RouterLink}
          to={`/acquirebookstatus/${id}/`}
          state={{
            acquiredListReturnTo: returnPath,
            acquiredListUserId: userId,
          }}
          startIcon={<ReadMoreIcon />}
          sx={{
            borderRadius: "3px",
            textTransform: "none",
            fontWeight: 700,
            transition: "all 0.15s ease",
          }}
        >
          詳細
        </MuiButton>
        <div style={{ display: "inline-block" }}>
          <MuiButton
            variant={ButtonVariantOption.Outlined}
            color="brand"
            onClick={() => {
              void handleDownload(id);
            }}
            startIcon={<DownloadIcon />}
            disabled={row.status === "REQUESTING"}
            sx={{
              borderRadius: "3px",
              textTransform: "none",
              fontWeight: 700,
              transition: "all 0.15s ease",
            }}
          >
            ダウンロード
          </MuiButton>
        </div>
        <ButtonMenu
          label="•••"
          variant={ButtonVariantOption.Text}
          sx={{
            minWidth: "40px",
            width: "40px",
            height: "40px",
            paddingX: 0,
            fontSize: "16px",
            fontWeight: "bold",
          }}
        >
          {realEstateRegistrationMonitoring ? (
            <ReadOnlyTooltip isReadOnly={isReadOnly}>
              <MenuItem
                onClick={() => {
                  setSelectedId(id);
                  setChargeCountClickedMonitoringExecuteButton(
                    row.requestPictureCount
                  );
                  setAcquireBookOpen(true);
                }}
                disabled={isReadOnly || row.status === "REQUESTING"}
                sx={{
                  py: 1,
                }}
              >
                モニタリング
              </MenuItem>
            </ReadOnlyTooltip>
          ) : null}
        </ButtonMenu>
      </Stack>
    );
  };
  const columns: GridColDef[] = [
    {
      field: "id",
      headerName: "No",
      width: 90,
      sortable: false,
      align: "right",
      headerAlign: "right",
    },
    {
      field: "name",
      headerName: "識別名",
      minWidth: 280,
      flex: 1,
      sortable: false,
      align: "left",
      headerAlign: "left",
      renderCell: ({
        value,
      }: GridRenderCellParams<string, AcquireBookStatusDataGridRow>) =>
        value ?? "",
    },
    {
      field: "requestedAt",
      headerName: "登記 / 図面依頼日",
      width: 220,
      sortable: false,
      align: "center",
      headerAlign: "center",
      renderCell: ({ value }: GridRenderCellParams<string>) => {
        if (value === undefined) return "";

        return format(new Date(value), "yyyy/MM/dd HH:mm");
      },
    },
    {
      field: OTHER_USER_RESOURCE_FIELD,
      headerName: "他ユーザー取得",
      width: 140,
      filterOperators: otherUserResourceFilterOperators,
      hide: !isSharableUser,
      sortable: false,
      align: "center",
      headerAlign: "center",
      valueFormatter: () => "",
      renderCell: ({
        value,
      }: GridRenderCellParams<boolean, AcquireBookStatusDataGridRow>) =>
        value ? <CheckIcon color="action" fontSize="small" /> : "",
    },
    {
      field: "requestPictureCount",
      headerName: "取得数（リクエスト数）",
      width: 180,
      sortable: false,
      align: "right",
      headerAlign: "right",
      renderCell: ({
        value,
      }: GridRenderCellParams<number, AcquireBookStatusDataGridRow>) =>
        formatNumberWithComma(value),
    },
    {
      field: "status",
      headerName: "取得状況",
      width: 150,
      sortable: false,
      align: "center",
      headerAlign: "center",
      renderCell: ({ value }: GridRenderCellParams<string>) => {
        return value === undefined ? null : (
          <RequestStatusChip status={value} />
        );
      },
    },
    {
      field: "action",
      headerName: "Action",
      minWidth: actionColumnMinWidth,
      sortable: false,
      align: "center",
      headerAlign: "center",
      renderCell: ({
        row,
      }: GridRenderCellParams<string, AcquireBookStatusDataGridRow>) =>
        renderActions(row),
    },
  ];
  return { columns, renderActions };
};

const usePictureRequest = (
  apiClient: AxiosInstance,
  userId: string,
  isSharable: boolean
): AcquireBookStatusDataGridRow[] => {
  const fetcher = async (url: string): Promise<PictureRequestAPIResponse> => {
    const { data } = await apiClient.get<PictureRequestAPIResponse>(url);
    validatePictureRequestListContract(data, url);
    return data;
  };

  const { data } = useSWR<PictureRequestAPIResponse>(
    userId ? ["/picture-request/", userId, isSharable] : null,
    async ([url]: [string, string, boolean]) => await fetcher(url),
    { revalidateOnFocus: true }
  );

  if (data === undefined) return [];
  return data.list;
};

interface RequestTableProps extends RequestFeedGridProps {
  rows: AcquiredRequest[];
  pagination?: {
    search: AcquiredPropertySearch;
    count: number;
    loading: boolean;
    hasError: boolean;
    onChange: (next: AcquiredPropertySearch) => void;
  };
}

/** 依頼全体への操作を旧一覧と共通検索の両方で利用する。 */
export const RequestTable: React.FC<RequestTableProps> = (props) => {
  const {
    setOpenMonitoringExecuteConfirmDialog,
    setChargeCountClickedMonitoringExecuteButton,
    setSelectedId,
    rows,
    pagination,
  } = props;
  const location = useLocation();
  const { realEstateRegistrationMonitoring } = useFeatureFlags();
  const { handleDownload } = useZipDownloadUrl();
  const isReadOnly = useReadOnlyUser();
  const userData = useContext(userDataContext);
  const isSharableUser = Boolean(userData.data.isSharable);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { columns: colDef, renderActions } = makeGridColDef(
    realEstateRegistrationMonitoring as boolean,
    isSharableUser,
    setSelectedId,
    setOpenMonitoringExecuteConfirmDialog,
    setChargeCountClickedMonitoringExecuteButton,
    handleDownload,
    isReadOnly,
    location.pathname + location.search,
    userData.data.id
  );

  if (isMobile && pagination) {
    return (
      <MobileList
        search={pagination.search}
        count={pagination.count}
        loading={pagination.loading}
        empty={!rows.length}
        emptyMessage={
          pagination.hasError
            ? "一覧を取得できませんでした"
            : "条件に一致する取得依頼がありません"
        }
        onChange={pagination.onChange}
      >
        {rows.map((row) => (
          <Card key={row.id} variant="outlined">
            <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 1,
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  No. {row.id}
                </Typography>
                <RequestStatusChip status={row.status} />
              </Box>
              <CardRow label="識別名">
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {row.name ?? "—"}
                </Typography>
              </CardRow>
              <CardRow label="登記 / 図面依頼日">
                <Typography variant="body2">
                  {format(new Date(row.requestedAt), "yyyy/MM/dd HH:mm")}
                </Typography>
              </CardRow>
              <CardRow label="取得数">
                <Typography variant="body2">
                  {formatNumberWithComma(row.requestPictureCount)}件
                </Typography>
              </CardRow>
              {isSharableUser && row.isOtherUserResource && (
                <CardRow label="取得ユーザー">
                  <Typography variant="body2">他ユーザー取得</Typography>
                </CardRow>
              )}
              <Box sx={{ mt: 1 }}>{renderActions(row)}</Box>
            </CardContent>
          </Card>
        ))}
      </MobileList>
    );
  }

  return (
    <StyledDataGrid
      stickyHeader={true}
      autoHeight
      disableSelectionOnClick={true}
      rows={rows}
      columns={
        pagination
          ? colDef.map((column) => ({
              ...column,
              filterable: false,
              sortable: false,
              disableColumnMenu: true,
            }))
          : colDef
      }
      initialState={
        pagination ? undefined : buildSelfAcquiredInitialState(isSharableUser)
      }
      {...(pagination && {
        loading: pagination.loading,
        rowCount: pagination.count,
        paginationMode: "server" as const,
        filterMode: "server" as const,
        sortingMode: "server" as const,
        page: pagination.search.page,
        pageSize: pagination.search.pageSize,
        rowsPerPageOptions: [25, 50, 100],
        onPageChange: (page: number) => {
          if (!pagination.loading && page !== pagination.search.page)
            pagination.onChange({ ...pagination.search, page });
        },
        onPageSizeChange: (pageSize: number) => {
          pagination.onChange({ ...pagination.search, pageSize, page: 0 });
        },
        components: {
          NoRowsOverlay: () => (
            <Stack alignItems="center" justifyContent="center" height="100%">
              {pagination.hasError
                ? "一覧を取得できませんでした"
                : "条件に一致する取得依頼がありません"}
            </Stack>
          ),
        },
      })}
      sortModel={[
        {
          field: "requestedAt",
          sort: "desc",
        },
      ]}
    />
  );
};

/** フラグOFF時は既存APIとブラウザ内フィルターを維持する。 */
export const RequestFeedGrid: React.FC<RequestFeedGridProps> = (props) => {
  const { apiClient } = useApiClient();
  const { data: user } = useContext(userDataContext);
  const rows = usePictureRequest(apiClient, user.id, Boolean(user.isSharable));
  return <RequestTable {...props} rows={rows} />;
};
